import React, { useState } from "react";
import {
  Anchor,
  Button,
  Group,
  Loader,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Link } from "react-router-dom";
import { useDelete, useTable } from "@refinedev/core";
import { IconSearch } from "@tabler/icons-react";

import type { Menu } from "../../types";

export function MenuList() {
  const [searchValue, setSearchValue] = useState("");

  const {
    tableQueryResult,
    current,
    setCurrent,
    pageCount,
    setFilters,
  } = useTable<Menu>({
    resource: "menus",
    pagination: {
      current: 1,
      pageSize: 10,
    },
    filters: {
      initial: [],
    },
  });

  const { mutateAsync: deleteAsync, isLoading: isDeleting } = useDelete();

  const isLoading = tableQueryResult.isLoading;

  /**
   * Delete a menu after confirmation. Calls DELETE /api/menus/:id.
   * Backend cascades to menu pages, menu items, and query logs.
   */
  const handleDeleteMenu = async (menu: Menu) => {
    const label = menu.restaurant?.name ?? menu.id;
    const confirmed = window.confirm(
      `Delete menu for "${label}"? This will permanently remove the menu, its pages, items, and query history.`
    );
    if (!confirmed) return;

    try {
      await deleteAsync({ resource: "menus", id: menu.id });
      notifications.show({
        title: "Menu deleted",
        message: `Menu for "${label}" has been deleted.`,
        color: "green",
      });
    } catch (e: unknown) {
      const message = e && typeof e === "object" && "message" in e ? String((e as { message: unknown }).message) : "Failed to delete menu";
      notifications.show({
        title: "Delete failed",
        message,
        color: "red",
      });
    }
  };
  const records = tableQueryResult.data?.data ?? [];
  
  // Debug: Log the response to see what we're getting
  if (tableQueryResult.isError) {
    console.error('Menu list error:', tableQueryResult.error);
  }

  // Handle search with debounce
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setFilters([
      {
        field: "search",
        operator: "eq",
        value: value || undefined,
      },
    ]);
    setCurrent(1); // Reset to first page on search
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Menus</Title>
      </Group>

      <TextInput
        placeholder="Search menus by ID, restaurant name, or admin notes..."
        leftSection={<IconSearch size={16} />}
        value={searchValue}
        onChange={(e) => handleSearch(e.currentTarget.value)}
        style={{ maxWidth: 400 }}
      />

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : (
        <>
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder style={{ minWidth: 600 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Restaurant</Table.Th>
                  <Table.Th>Menu ID</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Updated</Table.Th>
                  <Table.Th style={{ width: 200 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {records.map((menu: Menu) => (
                  <Table.Tr key={menu.id}>
                    <Table.Td>
                      <Text fw={600}>{menu.restaurant?.name ?? menu.restaurantId}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ fontFamily: "monospace" }}>
                        {menu.id}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {menu.createdAt ? new Date(menu.createdAt).toLocaleString() : "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {menu.updatedAt ? new Date(menu.updatedAt).toLocaleString() : "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Anchor component={Link} to={`/admin/menus/edit/v2/${menu.id}`}>
                          Edit
                        </Anchor>
                        <Anchor component={Link} to={`/menus/v2/${menu.id}`}>
                          Public
                        </Anchor>
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          color="red"
                          loading={isDeleting}
                          onClick={() => handleDeleteMenu(menu)}
                        >
                          Delete
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          {pageCount > 1 ? (
            <Group justify="center">
              <Pagination total={pageCount} value={current} onChange={setCurrent} />
            </Group>
          ) : null}
        </>
      )}
    </Stack>
  );
}
